<template>
  <div class="budget-detalhes">
    <div class="d-flex align-items-center mb-1">
      <b-button size="sm" variant="flat-secondary" @click="$router.push({ name: 'budget' })">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />Voltar
      </b-button>
      <h5 class="mb-0 ml-1">Detalhes do Budget</h5>
      <template v-if="budget">
        <b-badge :variant="(budget.categoria || 'ELT') === 'ELT' ? 'light-primary' : 'light-info'" class="ml-1">
          {{ budget.categoria || 'ELT' }}
        </b-badge>
        <small class="text-muted ml-50">
          {{ (budget.categoria || 'ELT') === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}
        </small>
      </template>
    </div>

    <div v-if="isLoading" class="text-center py-5"><b-spinner /></div>
    <template v-else-if="budget">
      <b-row>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Valor Total</small>
            <h3 class="mb-0">{{ formatarMoeda(budget.valor) }}</h3>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Gasto</small>
            <h3 class="mb-0 text-warning">{{ formatarMoeda(budget.valorgasto) }}</h3>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Saldo</small>
            <h3 class="mb-0" :class="saldo <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(saldo) }}
            </h3>
          </b-card>
        </b-col>
      </b-row>

      <b-card title="Informações">
        <b-row>
          <b-col md="6"><strong>Supervisor:</strong> {{ budget.supervisor_nome || budget.supervisorid }}</b-col>
          <b-col md="6"><strong>Categoria:</strong>
            <b-badge :variant="(budget.categoria || 'ELT') === 'ELT' ? 'light-primary' : 'light-info'">
              {{ budget.categoria || 'ELT' }}
            </b-badge>
            <small class="text-muted ml-25">
              {{ (budget.categoria || 'ELT') === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}
            </small>
          </b-col>
          <b-col md="6"><strong>Situação:</strong>
            <b-badge :variant="budget.ativo ? 'light-success' : 'light-secondary'">
              {{ budget.ativo ? 'Ativo' : 'Inativo' }}
            </b-badge>
          </b-col>
          <b-col md="6"><strong>Início:</strong> {{ formatarData(budget.datainicio) }}</b-col>
          <b-col md="6"><strong>Término:</strong> {{ formatarData(budget.datatermino) }}</b-col>
          <b-col v-if="budget.descricao" md="12" class="mt-50"><strong>Descrição:</strong> {{ budget.descricao }}</b-col>
        </b-row>
      </b-card>

      <b-card title="Ajuste manual de saldo">
        <p class="text-muted small mb-1">
          Aumente (valor positivo) ou reduza (valor negativo) o saldo. Escolha o
          destino: o supervisor (valor total orçado) ou um representante específico
          (alocação dele). O motivo é obrigatório e fica registrado no histórico.
        </p>
        <b-form @submit.prevent="aplicarAjuste">
          <b-row class="align-items-end">
            <b-col md="3" sm="6">
              <b-form-group label="Destino do ajuste *">
                <b-form-select v-model="ajuste.representanteid" :options="destinoOptions" />
              </b-form-group>
            </b-col>
            <b-col md="2" sm="6">
              <b-form-group label="Valor do ajuste *">
                <b-form-input
                  v-model.number="ajuste.valor"
                  type="number"
                  step="0.01"
                  placeholder="Ex.: 500 ou -200"
                />
              </b-form-group>
            </b-col>
            <b-col md="5" sm="12">
              <b-form-group label="Motivo *">
                <b-form-input
                  v-model="ajuste.motivo"
                  maxlength="255"
                  placeholder="Justificativa do ajuste"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" sm="6">
              <b-form-group label=" ">
                <b-button
                  block
                  variant="relief-primary"
                  type="submit"
                  :disabled="isSaving"
                >
                  <b-spinner v-if="isSaving" small class="mr-25" />
                  Aplicar
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <b-card title="Transferência de saldo">
        <p class="text-muted small mb-1">
          Realoca verba entre o supervisor (valor total orçado) e/ou um
          representante. A transferência move apenas a alocação — o valor total do
          budget não muda. Origem e destino devem ser diferentes. O motivo é
          obrigatório e fica registrado no histórico dos dois lados.
        </p>
        <b-form @submit.prevent="aplicarTransferencia">
          <b-row class="align-items-end">
            <b-col md="3" sm="6">
              <b-form-group label="Origem *">
                <b-form-select v-model="transfer.origemRepresentanteid" :options="destinoOptions" />
              </b-form-group>
            </b-col>
            <b-col md="3" sm="6">
              <b-form-group label="Destino *">
                <b-form-select v-model="transfer.destinoRepresentanteid" :options="destinoOptions" />
              </b-form-group>
            </b-col>
            <b-col md="2" sm="6">
              <b-form-group label="Valor *">
                <b-form-input
                  v-model.number="transfer.valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ex.: 500"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" sm="12">
              <b-form-group label="Motivo *">
                <b-form-input
                  v-model="transfer.motivo"
                  maxlength="255"
                  placeholder="Justificativa"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" sm="6">
              <b-form-group label=" ">
                <b-button
                  block
                  variant="relief-info"
                  type="submit"
                  :disabled="isSaving"
                >
                  <b-spinner v-if="isSaving" small class="mr-25" />
                  Transferir
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-card>

      <b-card title="Alocação por representante">
        <div v-if="!budget.alocacoes || !budget.alocacoes.length" class="text-muted">
          Sem alocações específicas — todo o valor é gerido pelo supervisor.
        </div>
        <b-table
          v-else
          :items="budget.alocacoes"
          :fields="alocFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(representante_nome)="data">
            {{ data.item.representante_nome || data.item.representanteid }}
          </template>
          <template #cell(valor)="data">{{ formatarMoeda(data.item.valor) }}</template>
          <template #cell(valorgasto)="data">{{ formatarMoeda(data.item.valorgasto) }}</template>
          <template #cell(saldo)="data">
            <span :class="Number(data.item.saldo) <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.saldo) }}
            </span>
          </template>
        </b-table>
      </b-card>

      <b-card title="Histórico de movimentações">
        <div v-if="!budget.logs || !budget.logs.length" class="text-muted">Nenhuma movimentação registrada.</div>
        <b-table
          v-else
          :items="budget.logs"
          :fields="logFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(datahora)="data">{{ formatarDataHora(data.item.datahora) }}</template>
          <template #cell(usuario_nome)="data">
            <span :title="data.item.usuario_id ? `ID ${data.item.usuario_id}` : ''">
              {{ data.item.usuario_nome || (data.item.usuario_id ? `Usuário ${data.item.usuario_id}` : '—') }}
            </span>
          </template>
          <template #cell(representante_nome)="data">{{ data.item.representante_nome || '-' }}</template>
          <template #cell(saldo_antes)="data">{{ formatarSaldo(data.item.saldo_antes) }}</template>
          <template #cell(saldo_depois)="data">{{ formatarSaldo(data.item.saldo_depois) }}</template>
          <template #cell(valor)="data">
            <span :class="Number(data.item.valor) < 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.valor) }}
            </span>
          </template>
        </b-table>
      </b-card>
    </template>
    <b-card v-else>
      <div class="text-center text-muted py-4">Budget não encontrado.</div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BSpinner, BTable, BBadge,
  BForm, BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue';
import { mapActions, mapState } from 'vuex';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'DetalhesBudget',
  components: {
    BCard, BRow, BCol, BButton, BSpinner, BTable, BBadge,
    BForm, BFormGroup, BFormInput, BFormSelect,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      ajuste: { valor: null, motivo: '', representanteid: null },
      transfer: {
        origemRepresentanteid: null, destinoRepresentanteid: null, valor: null, motivo: '',
      },
      alocFields: [
        { key: 'representante_nome', label: 'Representante' },
        { key: 'valor', label: 'Alocado', class: 'text-right' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      logFields: [
        { key: 'datahora', label: 'Data/Hora' },
        { key: 'usuario_nome', label: 'Quem fez' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'representante_nome', label: 'Representante' },
        { key: 'saldo_antes', label: 'Saldo antes', class: 'text-right' },
        { key: 'saldo_depois', label: 'Saldo depois', class: 'text-right' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapState('Budget', { budget: 'budgetAtual', isLoading: 'isLoading', isSaving: 'isSaving' }),
    saldo() {
      if (!this.budget) return 0;
      return Number(this.budget.valor || 0) - Number(this.budget.valorgasto || 0);
    },
    destinoOptions() {
      const opts = [{ value: null, text: 'Supervisor (valor total)' }];
      const alocacoes = (this.budget && this.budget.alocacoes) || [];
      alocacoes.forEach((a) => {
        opts.push({
          value: a.representanteid,
          text: `Representante: ${a.representante_nome || a.representanteid}`,
        });
      });
      return opts;
    },
  },
  mounted() {
    this.buscarPorId(this.$route.params.id);
  },
  methods: {
    ...mapActions('Budget', ['buscarPorId', 'ajusteManual', 'transferir']),
    async aplicarAjuste() {
      const valor = Number(this.ajuste.valor);
      if (!valor) {
        return this.aviso('Informe um valor de ajuste diferente de zero');
      }
      if (!this.ajuste.motivo || !this.ajuste.motivo.trim()) {
        return this.aviso('Informe o motivo do ajuste');
      }
      try {
        await this.ajusteManual({
          id: this.$route.params.id,
          payload: {
            valor,
            motivo: this.ajuste.motivo.trim(),
            representanteid: this.ajuste.representanteid || null,
          },
        });
        this.ajuste = { valor: null, motivo: '', representanteid: null };
        this.sucesso('Ajuste aplicado');
      } catch (e) {
        // erro já exibido pelo client
      }
      return null;
    },
    async aplicarTransferencia() {
      const valor = Number(this.transfer.valor);
      if (!valor || valor <= 0) {
        return this.aviso('Informe um valor de transferência maior que zero');
      }
      const origem = this.transfer.origemRepresentanteid || null;
      const destino = this.transfer.destinoRepresentanteid || null;
      if (origem === destino) {
        return this.aviso('Origem e destino devem ser diferentes');
      }
      if (!this.transfer.motivo || !this.transfer.motivo.trim()) {
        return this.aviso('Informe o motivo da transferência');
      }
      try {
        await this.transferir({
          id: this.$route.params.id,
          payload: {
            origemRepresentanteid: origem,
            destinoRepresentanteid: destino,
            valor,
            motivo: this.transfer.motivo.trim(),
          },
        });
        this.transfer = {
          origemRepresentanteid: null, destinoRepresentanteid: null, valor: null, motivo: '',
        };
        this.sucesso('Transferência realizada');
      } catch (e) {
        // erro já exibido pelo client
      }
      return null;
    },
    aviso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } });
    },
    sucesso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'CheckCircleIcon', variant: 'success' } });
    },
    formatarMoeda(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    // Saldo antes/depois pode ser null (ex.: lado do pool do supervisor numa
    // transferência, ou logs antigos anteriores à coluna) → exibe travessão.
    formatarSaldo(v) {
      if (v === null || v === undefined || v === '') return '—';
      return this.formatarMoeda(v);
    },
    formatarData(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
    formatarDataHora(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleString('pt-BR');
    },
  },
};
</script>
